import { useState, useRef, useEffect } from "react";

import { MdOutlineClear, MdOutlineArrowDropDown } from "react-icons/md";

import { BsXCircleFill } from "react-icons/bs";

import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import LoadingSpinner from "./LoadingSpinner";
import { TooltipStandard } from "./Utility/TooltipStandard";

/*
type BaseOption = {
  label: string;
  value: string;
};

type SingleSelectOption<T extends BaseOption> = {
  defaultOption?: T;
  multiple?: false;
  handleBlurSave?: (option: T) => void;
  limitButtonTags: undefined;
};

type MultiSelectOption<T extends BaseOption> = {
  defaultOption?: T[];
  multiple: true;
  handleBlurSave?: (option: T[]) => void;
  limitButtonTags?: number;
};

type AutoCompleteProps<T extends BaseOption> = {
  options: T[];
  getOptionLabel: (option: T) => string;
  renderInput: (props: InputHTMLAttributes<HTMLInputElement>) => JSX.Element;

  // Optional props
  sortOptions?: (options: T[]) => T[];
  handleBlurSave?: (option: T[]) => void;
  renderOptions?: (
    props: LiHTMLAttributes<HTMLLIElement>,
    key: string,
    option: T,
    selected: boolean,
  ) => JSX.Element;
  freesolo?: boolean;
  noOptionsText?: string;
  loading?: boolean;
} & (SingleSelectOption<T> | MultiSelectOption<T>);
*/

const validateRequiredProps = (options, getOptionLabel, renderInput) => {
  if (!options || !getOptionLabel || !renderInput) {
    throw new Error(
      "Missing required props for Autocomplete component, options, getOptionLabel, renderInput"
    );
  }

  // Validated renderInput && getOptionLabel is a function
  if (typeof getOptionLabel !== "function") {
    throw new Error("getOptionLabel must be a function");
  }
};

const filterData = (data, searchValue, getOptionLabel) =>
  data.filter((item) =>
    getOptionLabel(item).toLowerCase().includes(searchValue.toLowerCase())
  );

function AutoComplete({
  options,
  getOptionLabel,
  renderInput,

  // Optional Props
  defaultValue,
  multiple,
  renderOption,
  handleBlurSave,
  limitButtonTags = 5,
  freesolo,
  sortOptions,
  noOptionsText,
  loading,
  disabled,
}) {
  validateRequiredProps(options, getOptionLabel, renderInput);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const parentContainerRef = useRef(null);

  const [selectedOption, setSelectedOption] = useState([]);

  // set default value
  useEffect(() => {
    if (!defaultValue) return;

    if (multiple && defaultValue?.length > 0) {
      setSelectedOption(defaultValue);
    }

    if (!multiple) {
      setSelectedOption([defaultValue]);
    }
  }, [defaultValue, multiple]);

  // handle click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        parentContainerRef.current &&
        !parentContainerRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
        if (handleBlurSave) {
          handleBlurSave(selectedOption);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleBlurSave, selectedOption]);

  const filterOptionBySearchValue = sortOptions
    ? sortOptions(filterData(options, searchValue, getOptionLabel))
    : filterData(options, searchValue, getOptionLabel);

  const isOptionSelected = (option) =>
    selectedOption.some(
      (selected) => getOptionLabel(selected) === getOptionLabel(option)
    );

  // click clear options
  const clearOptions = (event) => {
    if (disabled) return;
    event.stopPropagation();

    setSelectedOption([]);
    setSearchValue("");
  };

  // click option select

  const selectOption = (event, option) => {
    event.stopPropagation();

    if (disabled) return;

    if (multiple) {
      if (isOptionSelected(option)) {
        setSelectedOption((prev) =>
          prev.filter(
            (selected) => getOptionLabel(selected) !== getOptionLabel(option)
          )
        );
      } else {
        setSelectedOption((prev) => [...prev, option]);
      }
    }

    if (
      !multiple &&
      getOptionLabel(option) !== getOptionLabel(selectedOption[0])
    ) {
      setShowDropdown(false);
      setSearchValue(getOptionLabel(option));
      setSelectedOption([option]);
    }
  };

  const handleOnKeyDown = (event) => {
    // If disabled, do not handle key down
    if (disabled) return;
    // Enter Key, free solo and search value is not empty, if not then return
    if (!(event.key === "Enter" && freesolo && searchValue.length > 0)) return;

    // check if search value is already selected
    const isSearchValueSelected = selectedOption.some(
      (option) => option.value === searchValue
    );
    if (isSearchValueSelected) return;

    // Add search value to selected option
    setSelectedOption((prev) => [
      ...prev,
      { label: searchValue, value: searchValue },
    ]);

    // Clear search value
    setSearchValue("");
  };

  return (
    <div
      className="autocomplete-container dropdown p-1 p-md-2"
      onClick={() => setShowDropdown(true)}
      ref={parentContainerRef}
    >
      {/* Filter Input */}
      <div className="autocomplete-input-container">
        <div className="autocomplete-option-btn-group">
          {multiple &&
            selectedOption
              .slice(0, showDropdown ? selectedOption.length : limitButtonTags)
              .map((option) => (
                <button
                  key={getOptionLabel(option)}
                  type="button"
                  className="btn btn-outline-dark"
                  onClick={(event) => {
                    if (disabled) return;
                    selectOption(event, option);
                  }}
                  disabled={disabled}
                >
                  {getOptionLabel(option)}
                  <span className="clear-btn shadow-none" aria-hidden="true">
                    <BsXCircleFill className="clickable ml-2 mb-1" />
                  </span>
                </button>
              ))}

          {/* Clear button  */}

          {multiple && selectedOption.length > 0 && (
            <button
              type="button"
              className="btn btn-outline-dark bg-grey"
              onClick={clearOptions}
              disabled={disabled}
            >
              Clear All
            </button>
          )}

          {multiple &&
            selectedOption.length > limitButtonTags &&
            !showDropdown && (
              <h5 className="my-0">
                +{selectedOption.length - limitButtonTags}
              </h5>
            )}
        </div>

        <div className="relative">
          {renderInput({
            type: "text",
            className: "form-control shadow-none outline-none",
            value: searchValue,
            onChange: (event) => setSearchValue(event.target.value),
            onKeyDown: handleOnKeyDown,
            disabled: disabled,
          })}
          {loading && (
            <LoadingSpinner className="search-icon absolute bottom-1/4 right-3" />
          )}
        </div>
      </div>

      {showDropdown && !disabled && (
        <>
          {/* Clear Icon */}
          <OverlayTrigger
            placement="top"
            overlay={
              <TooltipStandard id="tooltip-top">Clear All</TooltipStandard>
            }
          >
            <button
              type="button"
              className="btn shadow-none close"
              onClick={clearOptions}
            >
              <MdOutlineClear />
            </button>
          </OverlayTrigger>

          {/* Divider */}
          <div className="vertical-divider" />
        </>
      )}

      {/* Dropdown Icon */}
      {/* Up and Down Arrow */}
      <button
        className="btn shadow-none close"
        type="button"
        disabled={disabled}
        onClick={(event) => {
          event.stopPropagation();
          setShowDropdown((prev) => !prev);
        }}
      >
        <MdOutlineArrowDropDown
          className={`${showDropdown ? "rotate-180" : ""} duration-300`}
        />
      </button>

      {/* Dropdown Options */}
      {!disabled && (
        <ul
          className={`autocomplete-menu dropdown-menu ${
            showDropdown ? "show" : ""
          }`}
        >
          {!loading &&
            filterOptionBySearchValue.map((option) =>
              renderOption ? (
                renderOption(
                  {
                    className: "dropdown-item disable-select px-5 py-2",
                    key: getOptionLabel(option),
                    onMouseDown: (event) => selectOption(event, option),
                  },
                  option,
                  isOptionSelected(option)
                )
              ) : (
                <li
                  key={getOptionLabel(option)}
                  className="dropdown-item disable-select px-5 py-2"
                  onMouseDown={(event) => selectOption(event, option)}
                >
                  {getOptionLabel(option)}
                </li>
              )
            )}

          {!loading && filterOptionBySearchValue.length === 0 && (
            <li className="dropdown-item disable-select px-5 py-2">
              {noOptionsText || "No options found"}
            </li>
          )}

          {
            // Loading
            loading && (
              <li className="dropdown-item disable-select px-5 py-2">
                Loading...
              </li>
            )
          }
        </ul>
      )}
    </div>
  );
}

export default AutoComplete;
